import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import api, { bootstrapAccessToken, getAccessToken, refreshAccessToken } from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { getApiUrl } from '../config/runtimeConfig';

const SOCKET_URL = getApiUrl().replace(/\/api$/, '');

function isWhotRoute(pathname) {
  return /^\/play\/whot\/[^/]+$/.test(pathname);
}

function isCountdownRoute(pathname) {
  return /^\/match\/[^/]+\/countdown$/.test(pathname);
}

export default function StakeLifecycleListener() {
  const { user, initialized } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const socketRef = useRef(null);
  const navigatingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let socket = null;

    if (!initialized || !user) {
      socketRef.current?.disconnect();
      socketRef.current = null;
      navigatingRef.current = false;
      return undefined;
    }

    const navigateAuthoritative = (data) => {
      const matchId = data?.matchId || data?.gameId;
      if (!matchId) return;

      const gameType = String(data?.gameType || 'whot').toLowerCase();
      if (gameType !== 'whot') return;

      const state = String(data?.state || '').toLowerCase();
      if (['completed','settlement_pending','settling','cancelled','quarantined','finished','ended','gameover'].includes(state) || data?.settlementPending === true) {
        navigatingRef.current = false;
        return;
      }
      const currentPath = window.location.pathname;
      const target = state === 'in_progress'
        ? `/play/whot/${encodeURIComponent(matchId)}`
        : `/match/${encodeURIComponent(matchId)}/countdown`;

      if ((state === 'in_progress' && isWhotRoute(currentPath) && currentPath.endsWith(String(matchId))) ||
          (state !== 'in_progress' && isCountdownRoute(currentPath) && currentPath.endsWith(String(matchId) + '/countdown'))) {
        return;
      }

      if (navigatingRef.current) return;
      navigatingRef.current = true;

      navigate(target, {
        state: {
          matchSource: 'stake',
          matchId: String(matchId),
          tierId: data?.tierId || null,
          gameType: 'whot',
          playerCount: data?.playerCount,
          entryFee: data?.entryFee,
          players: Array.isArray(data?.players) ? data.players : [],
        },
        replace: true,
      });
    };

    const recoverActiveStake = async (currentSocket) => {
      try {
        const response = await api.stakes.getActive({ skipAuthRedirect: true });
        if (cancelled) return;

        const active = response.data?.data;
        if (!active) {
          navigatingRef.current = false;
          return;
        }

        const state = String(active.state || '').toLowerCase();

        if (active.tierId && currentSocket?.connected) {
          currentSocket.emit('stake:joined', { tierId: active.tierId });
        }

        if (active.matchId && ['matched', 'countdown', 'in_progress'].includes(state) && active.settlementPending !== true) {
          navigateAuthoritative(active);
        } else if (state === 'joined') {
          navigatingRef.current = false;
        }
      } catch (error) {
        console.warn('[StakeLifecycleListener] active stake recovery failed:', error);
        navigatingRef.current = false;
      }
    };

    const connect = async () => {
      let accessToken = getAccessToken();
      if (!accessToken) accessToken = await bootstrapAccessToken();
      if (!accessToken || cancelled) return;

      socket = io(SOCKET_URL, {
        auth: { token: accessToken },
        withCredentials: true,
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 500,
        reconnectionDelayMax: 5000,
      });
      socketRef.current = socket;

      socket.on('connect', () => {
        console.log('[StakeLifecycleListener] connected');
        void recoverActiveStake(socket);
      });

      socket.on('connect_error', async (error) => {
        const message = String(error?.message || '').toLowerCase();
        if (!message.includes('token') && !message.includes('expired') && !message.includes('authentication')) return;
        try {
          const fresh = await refreshAccessToken();
          if (fresh && socket) {
            socket.auth = { token: fresh };
            socket.connect();
          }
        } catch (_) {}
      });

      const handleReady = (data) => navigateAuthoritative({ ...data, state: data?.state || 'matched' });
      const handleState = (data) => {
        const state = String(data?.state || '').toLowerCase();
        if (!['matched', 'countdown', 'in_progress'].includes(state)) return;
        navigateAuthoritative(data);
      };

      socket.on('match:ready', handleReady);
      socket.on('stake:matched', handleReady);
      socket.on('stake:stateChanged', handleState);
      socket.on('match:started', (data) => navigateAuthoritative({ ...data, state: 'in_progress', gameType: 'whot' }));
      socket.on('stake:gameStarted', (data) => navigateAuthoritative({ ...data, state: 'in_progress', gameType: 'whot' }));
      socket.on('stake:gameCreationFailed', (data) => {
        const message = String(data?.message || 'The matched game could not be started. Your stake has been refunded.');
        console.error('[StakeLifecycleListener] game startup failed:', data);
        // The backend performs the refund before emitting stake:cancelled.
        // Show a clear failure instead of leaving the player on READY TO START.
        window.alert(message);
        navigatingRef.current = false;
        navigate('/', { replace: true, state: { message } });
      });
      socket.on('stake:cancelled', (data) => {
        if (!['server_error', 'startup_timeout'].includes(String(data?.reason || ''))) return;
        navigatingRef.current = false;
        navigate('/', { replace: true, state: { message: data?.message || 'Match cancelled, stake refunded.' } });
      });
      socket.on('reconnect', () => void recoverActiveStake(socket));
    };

    void connect();

    return () => {
      cancelled = true;
      socket?.disconnect();
      socketRef.current = null;
    };
  }, [initialized, user, navigate]);

  useEffect(() => {
    if (!initialized || !user) return;
    if (!isWhotRoute(location.pathname) && !isCountdownRoute(location.pathname)) {
      navigatingRef.current = false;
    }

    // Re-bind the authenticated lifecycle socket after every route transition.
    // Page-local stake sockets are allowed to disappear; this socket becomes
    // the authoritative live socket before the old page socket can trigger a
    // disconnect transition.
    const socket = socketRef.current;
    if (!socket?.connected) return;
    api.stakes.getActive({ skipAuthRedirect: true }).then((response) => {
      const active = response.data?.data;
      if (active?.tierId && socket.connected) {
        socket.emit('stake:joined', { tierId: active.tierId });
      }
    }).catch(() => {});
  }, [initialized, user, location.pathname]);

  return null;
}
